import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Section, StudentProgress, SectionProgress, Question, Answer } from '../types';
import { quizSections } from '../data/questions';

interface EvaluationLayoutProps {
  progress: StudentProgress;
  onComplete: () => void;
  onRetakeQuiz: () => void;
  onRetakeSection?: (sectionIndex: number, retakeType?: 'full' | 'partial') => void;
  onProgressUpdate?: (progress: StudentProgress) => void;
}

const EvaluationLayout: React.FC<EvaluationLayoutProps> = ({
  progress,
  onComplete,
  onRetakeQuiz,
  onRetakeSection,
  onProgressUpdate
}) => {
  const navigate = useNavigate();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const calculateSectionScore = (sectionProgress: SectionProgress, section: Section): number => {
    const totalQuestions = section.questions.length;
    const correctAnswers = (sectionProgress?.answers || []).filter(a => a.isCorrect).length;
    return totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
  };

  const getSectionStatus = (sectionProgress: SectionProgress, section: Section): 'passed' | 'needs-retake-questions' | 'needs-retake-section' => {
    const score = calculateSectionScore(sectionProgress, section);
    if (score === 100) return 'passed';

    const incorrectCount = section.questions.length - (sectionProgress?.answers || []).filter(a => a.isCorrect).length;
    const totalQuestions = section.questions.length;

    // If more than 50% incorrect, retake entire section
    if (incorrectCount > totalQuestions * 0.5) return 'needs-retake-section';

    return 'needs-retake-questions';
  };

  const renderMCQFeedback = (question: Question, answer: Answer) => {
    const isCorrect = answer.isCorrect;

    return (
      <div className={`p-6 rounded-lg border-2 ${
        isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
      }`}>
        <div className="flex items-start">
          <div className="flex-shrink-0 mr-4">
            {isCorrect ? (
              <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            )}
          </div>

          <div className="flex-1">
            <h4 className={`text-lg font-semibold mb-2 ${
              isCorrect ? 'text-green-800' : 'text-red-800'
            }`}>
              {isCorrect ? 'Correct!' : 'Incorrect'}
            </h4>

            {isCorrect ? (
              <div>
                <div className="text-green-700 mb-3 whitespace-pre-line">{answer.feedback || question.explanation}</div>
                <div className="bg-white p-3 rounded border">
                  <p className="font-medium text-gray-800 mb-1">Why this is correct:</p>
                  <p className="text-gray-700">
                    {question.options?.[question.correct || 0]} - {question.explanation}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <div className="text-red-700 mb-3 whitespace-pre-line">{answer.feedback}</div>

                <div className="space-y-3">
                  <div className="bg-white p-3 rounded border">
                    <p className="font-medium text-gray-800 mb-1">Your answer:</p>
                    <p className="text-red-600">
                      {answer.selectedOption !== undefined && question.options
                        ? question.options[answer.selectedOption]
                        : 'No answer selected'}
                    </p>
                    {answer.selectedOption !== undefined && question.options && (
                      <p className="text-sm text-gray-600 mt-1">
                        Why this is incorrect: This choice doesn't align with safety protocols and best practices.
                      </p>
                    )}
                  </div>

                  <div className="bg-white p-3 rounded border">
                    <p className="font-medium text-gray-800 mb-1">Correct answer:</p>
                    <p className="text-green-700 font-medium">
                      {question.options?.[question.correct || 0]}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {question.explanation}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderFreeResponseFeedback = (question: Question, answer: Answer) => {
    const isCorrect = answer.isCorrect;
    const detailedFeedback = answer.detailedFeedback;

    return (
      <div className={`p-6 rounded-lg border-2 ${
        isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
      }`}>
        <div className="flex items-start">
          <div className="flex-shrink-0 mr-4">
            {isCorrect ? (
              <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            )}
          </div>

          <div className="flex-1">
            <h4 className={`text-lg font-semibold mb-2 ${
              isCorrect ? 'text-green-800' : 'text-red-800'
            }`}>
              {isCorrect ? 'Correct Response!' : 'Needs Improvement'}
            </h4>

            <div className="space-y-4">
              <div className="bg-white p-3 rounded border">
                <p className="font-medium text-gray-800 mb-1">Your response:</p>
                <p className="text-gray-700">{answer.freeResponse || 'No response provided'}</p>
              </div>

              {answer.feedback && (
                <div className="bg-blue-25 p-3 rounded border border-blue-200">
                  <p className="font-medium text-blue-800 mb-2">Evaluation feedback:</p>
                  <div className="text-blue-700 whitespace-pre-line">{answer.feedback}</div>
                </div>
              )}

              {detailedFeedback?.correctPoints && detailedFeedback.correctPoints.length > 0 && (
                <div className="bg-green-25 p-3 rounded border border-green-200">
                  <p className="font-medium text-green-800 mb-2">✓ Correct points identified:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {detailedFeedback.correctPoints.map((point, index) => (
                      <li key={index} className="text-green-700">{point}</li>
                    ))}
                  </ul>
                </div>
              )}

              {detailedFeedback?.incorrectPoints && detailedFeedback.incorrectPoints.length > 0 && (
                <div className="bg-red-25 p-3 rounded border border-red-200">
                  <p className="font-medium text-red-800 mb-2">✗ Incorrect items:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {detailedFeedback.incorrectPoints.map((point, index) => (
                      <li key={index} className="text-red-700">{point}</li>
                    ))}
                  </ul>
                </div>
              )}

              {detailedFeedback?.missedPoints && detailedFeedback.missedPoints.length > 0 && (
                <div className="bg-orange-25 p-3 rounded border border-orange-200">
                  <p className="font-medium text-orange-800 mb-2">Additional points that could be included:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {detailedFeedback.missedPoints.map((point, index) => (
                      <li key={index} className="text-orange-700">{point}</li>
                    ))}
                  </ul>
                </div>
              )}

              {question.completeAnswer && (
                <div className="bg-blue-25 p-3 rounded border border-blue-200">
                  <p className="font-medium text-blue-800 mb-2">Complete reference answer:</p>
                  <p className="text-blue-700">{question.completeAnswer}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const currentSection = quizSections[currentSectionIndex];
  const currentSectionProgress = progress.sections[currentSectionIndex];
  const sectionScore = calculateSectionScore(currentSectionProgress, currentSection);
  const sectionStatus = getSectionStatus(currentSectionProgress, currentSection);

  const overallPassed = progress.sections.every(section => {
    const sectionData = quizSections.find(s => s.id === section.id)!;
    const score = calculateSectionScore(section, sectionData);
    return score === 100;
  });

  const handleViewCertificate = () => {
    // Mark certificate as viewed and navigate
    const updatedProgress = {
      ...progress,
      certificateViewed: true
    };
    onProgressUpdate?.(updatedProgress);
    navigate('/certificate');
  };

  const getOverallRetakeType = (): 'full' | 'partial' => {
    const failedSections = progress.sections.filter((section, index) => {
      const sectionData = quizSections[index];
      return calculateSectionScore(section, sectionData) < 100;
    });

    if (failedSections.length === 0) return 'full';

    // Check if any failed section needs full retake
    const needsFullRetake = failedSections.some((section, originalIndex) => {
      const sectionIndex = progress.sections.findIndex(s => s.id === section.id);
      const sectionData = quizSections[sectionIndex];
      const totalQuestions = sectionData.questions.length;
      const incorrectCount = totalQuestions - (section.answers || []).filter(a => a.isCorrect).length;
      return incorrectCount > totalQuestions * 0.5;
    });

    return needsFullRetake ? 'full' : 'partial';
  };

  const handleSmartRetake = () => {
    const retakeType = getOverallRetakeType();

    if (retakeType === 'full') {
      onRetakeQuiz();
    } else {
      // Find first failed section and retake it
      const firstFailedSectionIndex = progress.sections.findIndex((section, index) => {
        const sectionData = quizSections[index];
        return calculateSectionScore(section, sectionData) < 100;
      });

      if (firstFailedSectionIndex !== -1 && onRetakeSection) {
        const failedSection = progress.sections[firstFailedSectionIndex];
        const sectionData = quizSections[firstFailedSectionIndex];
        const totalQuestions = sectionData.questions.length;
        const incorrectCount = totalQuestions - (failedSection.answers || []).filter(a => a.isCorrect).length;
        const sectionRetakeType = incorrectCount > totalQuestions * 0.5 ? 'full' : 'partial';

        onRetakeSection(firstFailedSectionIndex, sectionRetakeType);
      }
    }
  };

  const nextSection = () => {
    if (currentSectionIndex < quizSections.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
      // Scroll to top of page when moving to next section
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      onComplete();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Section Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="text-center mb-4">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {currentSection.name} - Evaluation Results
            </h1>
            <p className="text-gray-600">{currentSection.description}</p>
          </div>

          {/* Section Score and Status */}
          <div className={`p-6 rounded-lg border-2 text-center ${
            sectionStatus === 'passed'
              ? 'bg-green-50 border-green-200'
              : 'bg-orange-50 border-orange-200'
          }`}>
            <div className={`text-4xl font-bold mb-2 ${
              sectionStatus === 'passed' ? 'text-green-600' : 'text-orange-600'
            }`}>
              {Math.round(sectionScore)}%
            </div>
            <p className="text-lg text-gray-700 mb-3">
              {(currentSectionProgress?.answers || []).filter(a => a.isCorrect).length} of {currentSection.questions.length} questions correct
            </p>

            {sectionStatus === 'passed' ? (
              <div className="text-green-800">
                <h3 className="text-xl font-semibold mb-1">Section Passed! ✓</h3>
                <p>Excellent work on this section.</p>
              </div>
            ) : sectionStatus === 'needs-retake-section' ? (
              <div className="text-orange-800">
                <h3 className="text-xl font-semibold mb-1">Section Requires Full Retake</h3>
                <p className="mb-4">You need to retake the entire section to pass.</p>
                {onRetakeSection && (
                  <button
                    onClick={() => onRetakeSection(currentSectionIndex, 'full')}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium"
                  >
                    Retake Entire Section
                  </button>
                )}
              </div>
            ) : (
              <div className="text-orange-800">
                <h3 className="text-xl font-semibold mb-1">Some Questions Need Retaking</h3>
                <p className="mb-4">You need to retake specific questions to pass this section.</p>
                {onRetakeSection && (
                  <button
                    onClick={() => onRetakeSection(currentSectionIndex, 'partial')}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium"
                  >
                    Retake Incorrect Questions
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Questions and Feedback */}
        <div className="space-y-6">
          {currentSection.questions.map((question, questionIndex) => {
            const answer = (currentSectionProgress?.answers || []).find(a => a.questionId === question.id);

            if (!answer) return null;

            return (
              <div key={question.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Question {questionIndex + 1}
                  </h3>
                  <p className="text-gray-700 text-lg">{question.question}</p>
                </div>

                {question.type === 'multiple-choice'
                  ? renderMCQFeedback(question, answer)
                  : renderFreeResponseFeedback(question, answer)
                }
              </div>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="mt-8 text-center">
          <div className="space-x-4">
            {currentSectionIndex < quizSections.length - 1 ? (
              <button
                onClick={nextSection}
                className="btn-primary px-8 py-3 text-lg"
              >
                Continue to Next Section
              </button>
            ) : overallPassed ? (
              <button
                onClick={handleViewCertificate}
                className="btn-primary px-8 py-3 text-lg"
              >
                View Certificate
              </button>
            ) : (
              <button
                onClick={handleSmartRetake}
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 text-lg rounded-lg font-medium"
              >
                {getOverallRetakeType() === 'partial' ? 'Retake Incorrect Questions' : 'Retake Quiz'}
              </button>
            )}

            {/* Show View Certificate button on all sections if overall passed */}
            {overallPassed && (
              <button
                onClick={handleViewCertificate}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg rounded-lg font-medium"
              >
                🏆 View Certificate
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluationLayout;